import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router";

import { IpcRest } from "@/hooks/ipc";
import {
  planChangesPageSchema,
  planesDelCatalogoSchema,
  type PlanChange,
  type PlanDelCatalogo,
} from "@/schemas/billing";
import { interpretarPendiente, ultimoRechazo } from "./cambioDePlan";
import { desenvolverCatalogo } from "./catalogoDePlanes";
import { useBillingScope } from "./useBillingScope";

const PENDIENTE = "plan-change-pendiente";
const HISTORIAL = "plan-changes";
const CATALOGO = "planes-ofrecibles";
/** Para refrescar el catálogo cuando cambió una oferta (trd-client#336). */
export const CATALOGO_QUERY_KEY = CATALOGO;
const RUTA = "/api/v1/business/billing/plan-change";

/**
 * El cambio de plan agendado, o `null` si no hay ninguno.
 *
 * `GET /api/v1/business/billing/plan-change` (trd-bff#272, carril del DUEÑO).
 * **Contesta 204 cuando no hay ninguno**, y eso es el caso normal — la lectura
 * del cuerpo vacío vive en `interpretarPendiente`, probada aparte.
 */
export function usePlanChangePendiente(enabled = true) {
  const navigate = useNavigate();
  const scope = useBillingScope();
  const query = useQuery<PlanChange | null>(
    [PENDIENTE, ...scope],
    async () =>
      interpretarPendiente(await IpcRest.get<unknown>({ url: RUTA }, navigate)),
    { enabled, staleTime: 30_000 }
  );
  return {
    pendiente: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * El historial de cambios — **la única fuente del motivo de un rechazo**.
 *
 * Octopus revalida al aplicar, y para cuando rechaza el pendiente ya no existe:
 * su `GET` contesta 204. Sin leer esto, el dueño ve que su plan no cambió y
 * nadie le dice por qué.
 */
export function usePlanChanges(enabled = true) {
  const navigate = useNavigate();
  const scope = useBillingScope();
  const query = useQuery(
    [HISTORIAL, ...scope],
    async () =>
      planChangesPageSchema.parse(
        await IpcRest.get<unknown>({ url: `${RUTA}s` }, navigate)
      ),
    { enabled, staleTime: 30_000 }
  );
  return {
    rechazo: ultimoRechazo(query.data?.items),
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Cancelar el cambio agendado.
 *
 * **Un 409 no es un fallo del usuario**: quiere decir que billing ya movió el
 * plan, así que el cambio se completa o se rechaza solo. El mensaje de octopus
 * lo explica y llega entero, así que se muestra tal cual en vez de un
 * "ocurrió un error" genérico.
 */
export function useCancelPlanChange() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return useMutation<unknown, Error, void>(
    async () => IpcRest.delete<unknown>({ url: RUTA }, navigate),
    {
      onSuccess: () => {
        qc.invalidateQueries([PENDIENTE]);
        qc.invalidateQueries([HISTORIAL]);
      },
    }
  );
}

/**
 * Los planes a los que el dueño se puede pasar (trd-bff#285).
 *
 * El borde ya devuelve **sólo los ofrecibles** —`status: "active"` Y
 * `is_public: true`, fail-closed— así que la pantalla **no filtra nada**: dibuja
 * lo que le llega. Filtrar de nuevo acá sería una segunda regla que se puede
 * desincronizar de la del borde.
 *
 * El sobre puede venir de cuatro formas y lo desenvuelve `desenvolverCatalogo`.
 */
export function usePlanesOfrecibles(enabled = true) {
  const navigate = useNavigate();
  const scope = useBillingScope();
  const query = useQuery<PlanDelCatalogo[]>(
    [CATALOGO, ...scope],
    async () =>
      planesDelCatalogoSchema.parse(
        desenvolverCatalogo(
          await IpcRest.get<unknown>(
            { url: "/api/v1/business/billing/plans" },
            navigate
          )
        )
      ),
    { enabled, staleTime: 5 * 60_000 }
  );
  return {
    planes: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Pedir el cambio de plan.
 *
 * `POST /api/v1/business/billing/plan-change` con `{ plan_id, interval_days }`.
 * **La cadencia es parte del cambio**: el mismo plan de mensual a anual no es un
 * no-op, y sin `interval_days` octopus conserva la vigente.
 *
 * Se agenda para el borde del ciclo siguiente: esto NO cambia el plan ahora.
 */
export function useRequestPlanChange() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { plan_id: string; interval_days?: number }
  >(async (body) => IpcRest.post<unknown>({ url: RUTA, body }, navigate), {
    onSuccess: () => {
      qc.invalidateQueries([PENDIENTE]);
      qc.invalidateQueries([HISTORIAL]);
    },
  });
}
